"use client";

import React, { useMemo, useState } from "react";
import { TrackedLink } from "@/components/common/TrackedLink";
import {
  characteristicColumns,
  CODES,
  codeOf,
  GRADE,
  groupBySpecies,
} from "@/lib/species-comparison";
import styles from "./SpeciesComparisonTable.module.scss";

const ALL = "";

/**
 * Species Comparison table (Table block, Table Type "Species Comparison"):
 * species pills, an AA → A highlight toggle and Y/N/S/O badges. Intro, notes
 * and CTAs are rendered on the server and passed in.
 */
export default function SpeciesComparisonTable({
  rows,
  columns,
  header,
  footer,
}: {
  rows: Record<string, string>[];
  columns: string[];
  header?: React.ReactNode;
  footer?: React.ReactNode;
}) {
  const [species, setSpecies] = useState(ALL);
  const [highlight, setHighlight] = useState(true);

  const groups = useMemo(() => groupBySpecies(rows, columns), [rows, columns]);
  const characteristics = useMemo(() => characteristicColumns(columns), [columns]);
  const grades = useMemo(
    () => [...new Set(groups.flatMap((g) => g.rows.map((r) => r[GRADE]?.trim()).filter(Boolean)))],
    [groups],
  );
  const usedCodes = useMemo(() => {
    const used = new Set<string>();
    for (const row of rows) for (const column of characteristics) {
      const code = codeOf(row[column] ?? "");
      if (code) used.add(code);
    }
    return Object.keys(CODES).filter((code) => used.has(code));
  }, [rows, characteristics]);

  const visible = species === ALL ? groups : groups.filter((g) => g.species === species);
  const gradeChange = grades.length > 1 ? `${grades[0]} → ${grades.at(-1)}` : "";

  return (
    <div className={styles.root}>
      {header && <div className="stack gap-4">{header}</div>}

      <div className={styles.controls}>
        <div className={styles.pills} role="group" aria-label="Filter by species">
          {[{ label: "All species", value: ALL }, ...groups.map((g) => ({ label: g.species, value: g.species }))].map(
            ({ label, value }) => (
              <button
                key={label}
                type="button"
                className={styles.pill}
                aria-pressed={species === value}
                onClick={() => setSpecies(value)}
              >
                {label}
              </button>
            ),
          )}
        </div>
        {gradeChange && (
          <button
            type="button"
            role="switch"
            aria-checked={highlight}
            className={styles.toggle}
            onClick={() => setHighlight((on) => !on)}
          >
            <span className={styles.track} aria-hidden="true">
              <span className={styles.thumb} />
            </span>
            Highlight {gradeChange} changes
            <span className={styles.toggleState}>· {highlight ? "On" : "Off"}</span>
          </button>
        )}
      </div>

      <ul className={styles.legend} aria-label="Key">
        {usedCodes.map((code) => (
          <li key={code}>
            <Badge code={code} /> {CODES[code]}
          </li>
        ))}
        {gradeChange && (
          <li>
            <span className={`${styles.swatch} ${styles.changed}`} aria-hidden="true" /> Differs between{" "}
            {grades[0]} and {grades.at(-1)}
          </li>
        )}
      </ul>

      <div className={styles.scroller} tabIndex={0} role="region" aria-label="Face characteristics table">
        <table className={styles.table}>
          <thead>
            <tr>
              <th scope="col" className={styles.speciesCol}>
                Species
              </th>
              <th scope="col">{GRADE}</th>
              {characteristics.map((column) => (
                <th key={column} scope="col">
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          {visible.map((group) => (
            <tbody key={group.species} className={styles.group}>
              {group.rows.map((row, index) => (
                <tr key={`${group.species}-${row[GRADE]}-${index}`}>
                  {index === 0 && (
                    <th scope="rowgroup" rowSpan={group.rows.length} className={styles.speciesCol}>
                      <span className={styles.speciesName}>
                        {group.species}
                        {group.note && (
                          <sup className={styles.note} aria-label={`Note ${group.note}`}>
                            {group.note}
                          </sup>
                        )}
                      </span>
                      {group.link && (
                        <TrackedLink href={group.link} className={styles.speciesLink}>
                          {group.linkText || "Species page"} <span aria-hidden="true">→</span>
                        </TrackedLink>
                      )}
                    </th>
                  )}
                  <th scope="row" className={styles.grade}>
                    {row[GRADE]}
                  </th>
                  {characteristics.map((column) => {
                    const value = row[column] ?? "";
                    const code = codeOf(value);
                    const changed = highlight && group.differs.has(column);
                    return (
                      <td key={column} className={changed ? styles.changed : undefined}>
                        {code ? <Badge code={code} /> : <span className={styles.text}>{value}</span>}
                        {changed && <span className="sr-only"> (differs between grades)</span>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          ))}
        </table>
      </div>

      {footer && <div className={styles.footer}>{footer}</div>}
    </div>
  );
}

function Badge({ code }: { code: string }) {
  return (
    <span className={styles.badge} data-code={code} title={CODES[code]}>
      <span aria-hidden="true">{code}</span>
      <span className="sr-only">{CODES[code]}</span>
    </span>
  );
}
